import React, { useState, useEffect } from 'react';
import { 
  Users, DollarSign, AlertCircle, Package, 
  TrendingUp, TrendingDown, Activity, Wifi,
  Clock, CheckCircle, XCircle, AlertTriangle,
  PhoneCall, CreditCard, BarChart3
} from 'lucide-react';
import { 
  AreaChart, Area, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';
import api from '../../services/api';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import StatCard from '../../components/Dashboard/StatCard';
import ChartCard from '../../components/Dashboard/ChartCard';
import FidelizacaoAlerts from '../../components/Dashboard/FidelizacaoAlerts';
import toast from 'react-hot-toast';

const AdminDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [boletosData, setBoletosData] = useState(null);
  const [contratosStats, setContratosStats] = useState(null);
  const [movimentacoesData, setMovimentacoesData] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const fetchDashboardData = async () => {
    try {
      setRefreshing(true);
      
      // Busca dados principais do dashboard
      const [dashboard, boletos, contratos, movimentacoes] = await Promise.all([
        api.get('/dashboard'),
        api.get(`/boletos/resumo-financeiro?mes=${new Date().getMonth() + 1}&ano=${new Date().getFullYear()}`),
        api.get('/contratos/estatisticas'),
        api.get(`/movimentacoes/fluxo-caixa?dataInicio=${format(new Date(), 'yyyy-MM-01')}&dataFim=${format(new Date(), 'yyyy-MM-dd')}`)
      ]);

      setDashboardData(dashboard.data);
      setBoletosData(boletos.data);
      setContratosStats(contratos.data);
      setMovimentacoesData(movimentacoes.data);
      
    } catch (error) {
      toast.error('Erro ao carregar dados do dashboard');
      console.error(error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
    const interval = setInterval(fetchDashboardData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  const { clientes, financeiro, suporte, infraestrutura, vendas } = dashboardData || {};

  // Dados para gráfico de clientes
  const clientesStatusData = [
    { name: 'Ativos', value: clientes?.ativos || 0, color: '#10b981' },
    { name: 'Suspensos', value: clientes?.suspensos || 0, color: '#f59e0b' },
    { name: 'Cancelados', value: clientes?.cancelados || 0, color: '#ef4444' },
  ];

  // Total e payload customizado para legenda (mostra nome + percentual)
  const clientesTotal = clientesStatusData.reduce((s, i) => s + (i.value || 0), 0) || 0;
  const clientesLegendPayload = clientesStatusData.map(entry => ({
    value: `${entry.name}: ${clientesTotal > 0 ? Math.round((entry.value / clientesTotal) * 100) : 0}%`,
    type: 'square',
    color: entry.color,
  }));

  // Dados para gráfico de chamados
  const chamadosStatusData = [
    { name: 'Abertos', value: suporte?.totais?.abertos || 0, color: '#3b82f6' },
    { name: 'Em Andamento', value: suporte?.totais?.emAndamento || 0, color: '#f59e0b' },
    { name: 'Resolvidos', value: suporte?.totais?.resolvidos || 0, color: '#10b981' },
    { name: 'Vencidos', value: suporte?.totais?.vencidos || 0, color: '#ef4444' },
  ];

  // Dados do fluxo de caixa
  const fluxoCaixaData = movimentacoesData?.movimentacoesDia?.map(dia => ({
    data: format(new Date(dia.data), 'dd/MM'),
    receitas: dia.receitas,
    despesas: dia.despesas,
    saldo: dia.saldo
  })) || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Dashboard Administrativo
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            {format(new Date(), "dd 'de' MMMM 'de' yyyy 'às' HH:mm", { locale: ptBR })}
          </p>
        </div>
        <button
          onClick={fetchDashboardData}
          disabled={refreshing}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
        >
          <BarChart3 className="w-4 h-4" />
          {refreshing ? 'Atualizando...' : 'Atualizar'}
        </button>
      </div>

      {/* Cards de Estatísticas Principais */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total de Clientes"
          value={clientes?.total || 0}
          subtitle={`${clientes?.ativos || 0} ativos`}
          icon={Users}
          trend={{
            value: clientes?.novosHoje || 0,
            label: 'novos hoje',
            isPositive: true
          }}
          color="blue"
        />
        
        <StatCard
          title="Receita Mensal"
          value={`R$ ${(financeiro?.receitaMes || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
          subtitle={`Ticket médio: R$ ${(financeiro?.ticketMedio || 0).toFixed(2)}`}
          icon={DollarSign}
          trend={{
            value: `${((financeiro?.receitaMes - financeiro?.despesasMes) / financeiro?.receitaMes * 100 || 0).toFixed(1)}%`,
            label: 'margem',
            isPositive: financeiro?.lucroMes > 0
          }}
          color="green"
        />
        
        <StatCard
          title="Chamados Abertos"
          value={suporte?.chamadosAbertos || 0}
          subtitle={`${suporte?.chamadosHoje || 0} hoje`}
          icon={AlertCircle}
          trend={{
            value: suporte?.tempoMedioResolucao || '0h',
            label: 'tempo médio',
            isPositive: false
          }}
          color="yellow"
        />
        
        <StatCard
          title="Equipamentos Ativos"
          value={infraestrutura?.equipamentosAtivos || 0}
          subtitle={`${infraestrutura?.equipamentosDisponiveis || 0} disponíveis`}
          icon={Wifi}
          trend={{
            value: `${(infraestrutura?.disponibilidadeRede || 0).toFixed(2)}%`,
            label: 'disponibilidade',
            isPositive: infraestrutura?.disponibilidadeRede >= 99
          }}
          color="purple"
        />
      </div>

      {/* Segunda linha de cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatCard
          title="Contratos Ativos"
          value={contratosStats?.ativos || 0}
          subtitle={`${contratosStats?.suspensos || 0} suspensos`}
          icon={Package}
          trend={{
            value: contratosStats?.aguardando || 0,
            label: 'aguardando ativação',
            isPositive: false
          }}
          color="indigo"
        />
        
        <StatCard
          title="Inadimplência"
          value={`${(financeiro?.inadimplencia || 0).toFixed(1)}%`}
          subtitle={`${boletosData?.quantidadeBoletos || 0} boletos emitidos`}
          icon={CreditCard}
          trend={{
            value: `R$ ${(boletosData?.totalVencido || 0).toLocaleString('pt-BR')}`,
            label: 'em atraso',
            isPositive: false
          }}
          color="red"
        />
        
        <StatCard
          title="Meta de Vendas"
          value={`${(vendas?.percentualMeta || 0).toFixed(0)}%`}
          subtitle={`${vendas?.novasVendasMes || 0} de ${vendas?.metaMes || 0}`}
          icon={TrendingUp}
          trend={{
            value: vendas?.crescimentoLiquido || 0,
            label: 'crescimento líquido',
            isPositive: vendas?.crescimentoLiquido > 0
          }}
          color="teal"
        />
      </div>

      {/* Gráficos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Gráfico de Status de Clientes */}
        <ChartCard title="Distribuição de Clientes">
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={clientesStatusData}
                cx="50%"
                cy="50%"
                // não mostrar rótulos dentro das fatias para evitar quebras
                label={false}
                labelLine={false}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {clientesStatusData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip formatter={(value, name) => [`${value}`, name]} />
              {/* Usamos um payload customizado na legenda para mostrar nome + percentual sem quebra */}
              <Legend verticalAlign="bottom" payload={clientesLegendPayload} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Gráfico de Chamados */}
        <ChartCard title="Status dos Chamados">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chamadosStatusData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="value" fill="#3b82f6">
                {chamadosStatusData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Fluxo de Caixa */}
        <ChartCard title="Fluxo de Caixa" className="lg:col-span-2">
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={fluxoCaixaData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="data" />
              <YAxis />
              <Tooltip formatter={(value) => `R$ ${value.toLocaleString('pt-BR')}`} />
              <Legend />
              <Line type="monotone" dataKey="receitas" stroke="#10b981" name="Receitas" />
              <Line type="monotone" dataKey="despesas" stroke="#ef4444" name="Despesas" />
              <Line type="monotone" dataKey="saldo" stroke="#3b82f6" name="Saldo" />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Alertas de Fidelização */}
      <FidelizacaoAlerts />

      {/* Alertas e Notificações */}
      {infraestrutura?.alarmesCriticos > 0 && (
        <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 p-4 rounded">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 text-red-500 mr-3" />
            <div>
              <p className="text-sm font-medium text-red-800 dark:text-red-300">
                {infraestrutura.alarmesCriticos} alarmes críticos na infraestrutura
              </p>
              <p className="text-sm text-red-700 dark:text-red-400">
                Verifique os equipamentos com urgência
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;